/* @flow */

import { DateTime } from 'luxon';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  isBraindateCancelled,
  isBraindateDeclined,
  getBraindateLastActiveUser,
  getBraindateTimeSlot,
} from '@e180/domain/lib/braindate/util';
import { getUserId } from '@e180/domain/lib/user/util';

import Timezone from 'src/shared/app/base/date/component/Timezone';
import useTimezone from 'src/shared/app/base/component/hook/useTimezone';
import useTimeFormat from 'src/shared/app/base/date/component/hook/useTimeFormat';
import dateMessages from 'src/shared/app/base/date/l10n/dateL10n';
import {
  formatTime,
  formatMonthDay,
} from 'src/shared/app/base/date/util/dateFormatters';
import useVirtualBraindate from 'plugin/virtual-braindate/hook/useVirtualBraindate';
import braindateMessages from 'src/shared/app/braindate/l10n/braindateL10n';
import useSelf from 'src/shared/domain/user/hook/useSelf';

import type { Braindate } from '@e180/domain/lib/braindate/type';

type Props = {
  braindate: Braindate,
};

const BraindateCardStatus = ({ braindate }: Props): React.Node => {
  const date = getBraindateTimeSlot(braindate);
  const lastActiveUser = getBraindateLastActiveUser(braindate);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const user = useSelf();
  const timeFormat = useTimeFormat();
  const zone = useTimezone();
  const isVirtualBraindateEnabled = useVirtualBraindate();

  const { locale } = intl;
  const lastActiveUserId = lastActiveUser && getUserId(lastActiveUser);
  const isLastActiveUser = user && lastActiveUserId === getUserId(user);

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  if (isBraindateDeclined(braindate)) {
    if (isLastActiveUser) {
      return intl.formatMessage(braindateMessages.youDeclined);
    }

    return intl.formatMessage(braindateMessages.theyDeclined);
  }

  if (isBraindateCancelled(braindate)) {
    if (isLastActiveUser) {
      return intl.formatMessage(braindateMessages.youCancelled);
    }

    return intl.formatMessage(braindateMessages.theyCancelled);
  }

  if (date) {
    const { start_time: start } = date;
    const startTime = DateTime.fromISO(start, { zone });
    const text = intl.formatMessage(dateMessages.dayAtTime, {
      day: formatMonthDay(startTime, locale),
      time: formatTime(startTime, locale, timeFormat),
    });

    const zoneElt = isVirtualBraindateEnabled && <Timezone withoutCity />;

    return (
      <React.Fragment>
        <time dateTime={startTime.toISO()} aria-label={text}>
          {text} {zoneElt}
        </time>
      </React.Fragment>
    );
  }

  if (isLastActiveUser) {
    return intl.formatMessage(braindateMessages.sentInvitation);
  }

  return intl.formatMessage(braindateMessages.newInvitation);
};

export default compose(React.memo)(BraindateCardStatus);
